import React from 'react';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import VideoPlayer from './VideoPlayer';

const Footer = ({ content }) => {
  if (!content) return null;
  
  const { footer, contact, video, siteName } = content;

  return (
    <>
      {/* Video Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {video.title}
            </h2>
            <p className="text-lg text-slate-600">{video.description}</p>
          </div>
          <VideoPlayer
            videoUrl={video.videoUrl}
            placeholder={video.placeholder}
            title={video.title}
            description="Upload your property tour video"
            editable={true}
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* About */}
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-bold mb-4">
                {siteName}
              </h3>
              <p className="text-slate-300 mb-6 leading-relaxed">
                {footer.about}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="#vision"
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    Vision
                  </a>
                </li>
                <li>
                  <a
                    href="#architecture"
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    Architecture
                  </a>
                </li>
                <li>
                  <a
                    href="#plan"
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    Plans
                  </a>
                </li>
                <li>
                  <a
                    href="#revenue"
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    Revenue Model
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-slate-300">
                  <Mail className="w-4 h-4" />
                  <a
                    href={`mailto:${contact.email}`}
                    className="hover:text-white transition-colors"
                  >
                    {contact.email}
                  </a>
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <Phone className="w-4 h-4" />
                  <a
                    href={`tel:${contact.phone}`}
                    className="hover:text-white transition-colors"
                  >
                    {contact.phone}
                  </a>
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span>{contact.address}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Social Media */}
          <div className="flex justify-center gap-6 mb-8 pb-8 border-b border-slate-700">
            <a
              href={contact.socialMedia.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href={contact.socialMedia.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-slate-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={contact.socialMedia.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-slate-800 hover:bg-blue-700 rounded-full flex items-center justify-center transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-5 h-5" />
            </a>
          </div>

          {/* Disclaimer */}
          <div className="bg-slate-800 rounded-lg p-6 mb-8">
            <h4 className="text-sm font-semibold text-amber-400 mb-2">
              Disclaimer
            </h4>
            <p className="text-xs text-slate-300 leading-relaxed">
              {footer.disclaimer}
            </p>
          </div>

          {/* Copyright */}
          <div className="text-center text-sm text-slate-400">
            <p>{footer.copyright}</p>
            <div className="mt-4">
              <a 
                href="/admin/login" 
                className="text-slate-400 hover:text-blue-400 transition-colors text-sm underline"
              >
                Admin Login
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
